// Shared visual design tokens for the redesigned frontend pages
// (Home, Contact Us, Testimonials, How to Order). Keep this palette
// consistent across those pages instead of hardcoding colors per file.

export const colors = {
  ink: "#1A2530",
  ink2: "#47536A",
  ink3: "#7A8699",
  paper: "#FCFBF9",
  paperTint: "#E9F4F5",
  paperTintWarm: "#FBEEE4",
  line: "#E2E6EA",
  primary: "#1C6B76",
  primaryDark: "#134B54",
  primaryLight: "#DCEEF0",
  accent: "#E8622E",
  accentDark: "#C94F21",
  accentLight: "#FBE7DD",
  white: "#FFFFFF",
};

// Lightens (percent > 0) or darkens (percent < 0) a "#rrggbb" color.
function shadeColor(hex, percent) {
  if (!hex || hex[0] !== "#" || hex.length !== 7) return hex;
  const f = parseInt(hex.slice(1), 16);
  const t = percent < 0 ? 0 : 255;
  const p = percent < 0 ? -percent : percent;
  const R = f >> 16;
  const G = (f >> 8) & 0x00ff;
  const B = f & 0x0000ff;
  return (
    "#" +
    (
      0x1000000 +
      (Math.round((t - R) * p) + R) * 0x10000 +
      (Math.round((t - G) * p) + G) * 0x100 +
      (Math.round((t - B) * p) + B)
    )
      .toString(16)
      .slice(1)
  );
}

// Mutates the shared `colors` object in place so every file that imported
// `colors` (via sx props, evaluated fresh on each render) picks up the
// change on next render, with no per-file plumbing needed. Driven by
// Website Settings -> Site Design (see SiteDesignSettings.jsx), applied
// once webSettings loads (see App.js).
export function applyThemeColors({ primary, accent } = {}) {
  if (primary && primary !== colors.primary) {
    colors.primary = primary;
    colors.primaryDark = shadeColor(primary, -0.25);
    colors.primaryLight = shadeColor(primary, 0.85);
  }
  if (accent && accent !== colors.accent) {
    colors.accent = accent;
    colors.accentDark = shadeColor(accent, -0.25);
    colors.accentLight = shadeColor(accent, 0.85);
  }
}

export const radii = {
  lg: "20px",
  md: "12px",
  full: "999px",
};

export const shadows = {
  card: "0 1px 2px rgba(26,37,48,0.06), 0 10px 28px -14px rgba(26,37,48,0.22)",
  cardHover:
    "0 1px 2px rgba(26,37,48,0.08), 0 18px 36px -16px rgba(26,37,48,0.3)",
};

export const fonts = {
  display: "'Plus Jakarta Sans', ui-sans-serif, system-ui, sans-serif",
  body: "'IBM Plex Sans', ui-sans-serif, system-ui, sans-serif",
};

// Reusable sx fragments
export const sectionCardSx = {
  borderRadius: radii.lg,
  boxShadow: shadows.card,
  border: `1px solid ${colors.line}`,
  bgcolor: colors.white,
};

export const eyebrowSx = {
  display: "inline-flex",
  alignItems: "center",
  gap: "8px",
  fontSize: 13,
  fontWeight: 600,
  letterSpacing: "0.06em",
  textTransform: "uppercase",
  color: colors.primary,
  bgcolor: colors.primaryLight,
  px: 1.75,
  py: 0.75,
  borderRadius: radii.full,
};
